import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  RefreshCw, Loader2, AlertCircle, AlertTriangle, ChevronDown, ChevronRight,
  Printer, Info,
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Canon — Canlı Kampanya Raporu (kampanya adından ayrıştırma).
 *
 * Meta hesabında iki iş yan yana yürüyor ve tek ayırt edici sinyal kampanya adı:
 *   1) "Kadrajın Ötesinde" video yarışması (Beyond The Frame) — 30 Haz → 16 Ağu
 *   2) Temmuz 2026 sosyal medya postları — 1 → 31 Tem
 * Reklam/adset adları jenerik bırakıldığı için ad-seviyesi eşleştirme
 * yapılamıyor (cf. /canon/cea-report).
 *
 * Veri CANLI Meta Graph API'den gelir (DB'den değil): GET /api/canon-report/by-name.
 */

const API_URL = 'https://api.glowytics.com';
const CANON_BRAND_ID = '3987361f-4006-4819-bd83-f78b5a2155ec';
const CANON_RED = '#CC0000';

const fmt = (n) => (n == null ? '—' : Math.round(n).toLocaleString('tr-TR'));
const money = (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('tr-TR')} ₺`);
const pct = (n) => (n == null ? '—' : `%${Number(n).toFixed(2)}`);
const dec = (n) => (n == null ? '—' : Number(n).toFixed(2));

const STATUS_CLS = {
  ACTIVE: 'bg-emerald-100 text-emerald-700',
  PAUSED: 'bg-gray-100 text-gray-600',
};

// Zero-width space'ler ekranda görünmez bir "hayalet" boşluk bırakıyor; ad
// alanının bozuk olduğu bu sayfanın konusu olduğu için görünür işaretlenir.
const ZWSP = new RegExp('[\\u200B-\\u200F\\uFEFF]', 'g');
const cleanName = (s) => String(s || '').replace(ZWSP, '');

// Dört ayrı iş, dört ayrı panel. Summer Promo ve UGC, Temmuz akışının içinden
// çıkarılıp kendi başlarına raporlanır; "Diğer Postlar" geriye kalanı taşır.
const PANELS = [
  {
    group: 'btf',
    title: 'Kadrajın Ötesinde — Video Yarışması',
    subtitle: 'Beyond The Frame · paid akış 30 Haziran – 16 Ağustos 2026',
    bucketTitle: 'Format Kırılımı',
    bucketHead: 'Format',
    defaultSince: '2026-06-30',
    defaultUntil: '2026-08-16',
    // Trafik hedefli yarışma: tık/CTR/CPC birincil.
    primary: 'traffic',
  },
  {
    group: 'summer',
    title: 'PV — Summer Promo',
    subtitle: 'Yaz promosyonu · Temmuz akışının en büyük kalemi',
    bucketTitle: 'Format Kırılımı',
    bucketHead: 'Format',
    defaultSince: '2026-07-01',
    defaultUntil: '2026-07-31',
    primary: 'traffic',
    // Kreatif + gerçekleşen rakam bloğu (Megatank raporundaki MEDYA düzeni).
    media: true,
  },
  {
    group: 'ugc',
    title: 'UGC İçerikleri',
    subtitle: "CEA sheet'te «Future of People» · Reshare + Theme Announcement",
    bucketTitle: 'UGC Alt Kırılımı',
    bucketHead: 'İçerik',
    defaultSince: '2026-07-01',
    defaultUntil: '2026-07-31',
    // Etkileşim hedefli: etkileşim birincil.
    primary: 'engagement',
  },
  {
    // "Artık kampanya" değil — CEA sheet'inin kendi frame'i. Tuesday Tip ve
    // Technology Highlight aynı frame altında, ikisi de Traffic + ₺5.000 boost.
    group: 'july',
    title: "Future of What's Possible",
    subtitle: 'Tuesday Tip & Technology Highlight · haftalık ipucu + ürün vitrini',
    bucketTitle: 'İçerik Kırılımı',
    bucketHead: 'İçerik',
    defaultSince: '2026-07-01',
    defaultUntil: '2026-07-31',
    primary: 'traffic',
  },
];

function Tile({ label, value, sub, tone = 'gray' }) {
  const tones = {
    gray: 'border-gray-200 bg-gray-50 text-gray-800',
    red: 'border-red-100 bg-red-50 text-red-700',
    indigo: 'border-indigo-100 bg-indigo-50 text-indigo-700',
    green: 'border-emerald-100 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <span className="text-[10px] font-medium uppercase opacity-70">{label}</span>
      <p className="text-lg font-bold mt-0.5 tabular-nums">{value}</p>
      {sub && <p className="text-[10px] opacity-60 mt-0.5 leading-tight">{sub}</p>}
    </div>
  );
}

function CreativeRows({ campaignId, since, until }) {
  const [ads, setAds] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/canon-report/by-name/creatives`, {
      params: { campaignIds: campaignId, since, until },
      headers: { 'X-Brand-ID': CANON_BRAND_ID },
      withCredentials: true,
    })
      .then(({ data }) => { if (alive) setAds(data.ads || []); })
      .catch((e) => { if (alive) setErr(e.response?.data?.error || e.message); });
    return () => { alive = false; };
  }, [campaignId, since, until]);

  if (err) return <div className="px-4 py-3 text-xs text-red-600">Kreatifler alınamadı: {err}</div>;
  if (!ads) {
    return (
      <div className="px-4 py-3 text-xs text-gray-400 flex items-center gap-2">
        <Loader2 className="w-3 h-3 animate-spin" /> Kreatifler yükleniyor…
      </div>
    );
  }
  if (!ads.length) return <div className="px-4 py-3 text-xs text-gray-400">Bu pencerede harcaması olan reklam yok.</div>;

  return (
    <div className="px-4 py-3 bg-gray-50/70">
      <p className="text-[10px] uppercase font-semibold text-gray-500 mb-2">Reklamlar ({ads.length})</p>
      <div className="space-y-2">
        {ads.map((a) => (
          <div key={a.adId} className="flex gap-3 bg-white rounded-lg border border-gray-200 p-2">
            {a.creativeId ? (
              // Meta CDN URL'leri saatler içinde ölüyor; proxy her istekte taze
              // imzalı URL çözüp byte'ları geri akıtır (cf. /creative-image).
              <img
                src={`${API_URL}/api/canon-report/creative-image/${a.creativeId}`}
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
                alt=""
                className="w-14 h-14 object-cover rounded flex-shrink-0 bg-gray-100"
              />
            ) : (
              <div className="w-14 h-14 rounded bg-gray-100 flex-shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-gray-800 truncate">{a.adName}</p>
              {a.body && <p className="text-[10px] text-gray-500 line-clamp-2 mt-0.5">{a.body}</p>}
              <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[10px] text-gray-600 tabular-nums">
                <span><strong>{money(a.spend)}</strong></span>
                <span>{fmt(a.impressions)} gst</span>
                <span>{fmt(a.reach)} erş</span>
                <span>{fmt(a.clicks)} tık</span>
                <span>CTR {pct(a.ctr)}</span>
                <span>CPC {dec(a.cpc)}₺</span>
                <span className="text-indigo-600 font-semibold">CPM {dec(a.cpm)}₺</span>
                <span>etk. {fmt(a.isVideo ? a.engagement - a.videoViews : a.engagement)}</span>
                {a.isVideo && <span className="text-indigo-600">video izl. {fmt(a.videoViews)}</span>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * MEDYA — kampanya bazında kullanılan kreatifler, her görselin ALTINDA o
 * kampanyanın gerçekleşen rakamları. Düzen /canon/megatank-report'taki MEDYA
 * bloğunun aynısı; tek fark görsellerin kaynağı: Megatank Meta CDN URL'lerini
 * doğrudan kullanıyor, burada /creative-image proxy'si kullanılır çünkü CDN
 * URL'leri saatler içinde ölüyor (cf. CreativeRows).
 *
 * Rakamlar kreatif çağrısından DEĞİL, panelin zaten çektiği kampanya
 * satırlarından gelir — böylece tablodaki değerlerle birebir aynı kalır.
 */
function MediaBlock({ campaigns, since, until }) {
  const [ads, setAds] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);

  const ids = campaigns.map((c) => c.id).join(',');

  const load = useCallback(async () => {
    if (!ids) { setAds([]); return; }
    setLoading(true); setErr(null);
    try {
      const { data } = await axios.get(`${API_URL}/api/canon-report/by-name/creatives`, {
        params: { campaignIds: ids, since, until },
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        withCredentials: true,
      });
      setAds(data.ads || []);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [ids, since, until]);

  useEffect(() => { load(); }, [load]);

  // Aynı içeriğin FB ve IG kopyaları ayrı reklamdır ama çoğu zaman aynı
  // creative'i taşır; görsel iki kez basılmasın diye creativeId'ye göre teklenir.
  const byCampaign = new Map();
  (ads || []).forEach((a) => {
    if (!byCampaign.has(a.campaignId)) byCampaign.set(a.campaignId, []);
    byCampaign.get(a.campaignId).push(a);
  });

  const ordered = [...campaigns].sort((a, b) => (b.spend || 0) - (a.spend || 0));

  return (
    <div>
      <div className="flex items-start gap-3 mb-3">
        <span className="inline-block bg-indigo-600 text-white text-[10px] font-bold px-2.5 py-1 rounded-md flex-shrink-0">MEDYA</span>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-800">Kampanya Bazında Kullanılan Medya</p>
          <p className="text-[11px] text-gray-500 mt-0.5">
            Her kampanyanın kreatifleri ve altında gerçekleşen rakamları — <strong>canlı</strong> Meta Graph API.
          </p>
        </div>
      </div>

      {err && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          Kreatifler alınamadı: {err}
        </div>
      )}
      {loading && !ads && (
        <div className="py-8 flex justify-center items-center gap-2 text-xs text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Kreatifler çekiliyor…
        </div>
      )}

      {ads && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {ordered.map((c) => {
            const list = byCampaign.get(c.id) || [];
            const seen = new Set();
            const shots = list.filter((a) => {
              if (!a.creativeId || seen.has(a.creativeId)) return false;
              seen.add(a.creativeId);
              return true;
            });
            const cVideoHeavy = c.engagement > 0 && c.videoViews / c.engagement > 0.2;
            const stats = [
              { label: 'Harcama', val: money(c.spend) },
              { label: 'Gösterim', val: fmt(c.impressions) },
              { label: 'Erişim', val: fmt(c.reach) },
              { label: 'Tıklama', val: fmt(c.clicks) },
              { label: 'CTR', val: pct(c.ctr) },
              { label: 'CPM', val: `${dec(c.cpm)} ₺` },
            ];
            return (
              <div key={c.id} className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-slate-50 px-4 py-2.5 border-b border-gray-100">
                  <p className="text-[11px] font-mono text-gray-700 break-words" title={cleanName(c.name)}>
                    {cleanName(c.name)}
                  </p>
                  <div className="flex flex-wrap items-center gap-1 mt-1">
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">
                      {c.format?.label}
                    </span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-600'}`}>
                      {c.status}
                    </span>
                    <span className="text-[9px] text-gray-400">{list.length} reklam</span>
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {shots.length === 0 ? (
                    <p className="text-[11px] text-gray-400 text-center py-6">Kreatif bulunamadı.</p>
                  ) : (
                    <div className="flex flex-wrap justify-center gap-2">
                      {shots.map((a) => (
                        <div key={a.adId} className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-100"
                          title={a.adName || ''}>
                          <img
                            src={`${API_URL}/api/canon-report/creative-image/${a.creativeId}`}
                            onError={(e) => { e.currentTarget.closest('div').style.display = 'none'; }}
                            alt={a.adName || 'kreatif'}
                            className="h-72 w-auto object-contain"
                            loading="lazy"
                          />
                          {a.isVideo && (
                            <span className="absolute bottom-1.5 right-1.5 bg-black/70 text-white text-[9px] px-2 py-0.5 rounded-full">
                              ▶ video
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {stats.map((s) => (
                      <div key={s.label} className="rounded-lg bg-gray-50 border border-gray-100 px-2 py-2 text-center">
                        <p className="text-[9px] text-gray-400 uppercase tracking-wide">{s.label}</p>
                        <p className="text-sm font-semibold text-gray-800 tabular-nums">{s.val}</p>
                      </div>
                    ))}
                  </div>
                  {cVideoHeavy && (
                    <p className="text-[9px] text-indigo-600 text-center">
                      Etkileşimin {fmt(c.videoViews)} adedi video izlenmesi — video hariç etkileşim {fmt(c.engagementExVideo)}.
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-gray-400 mt-2">
        Rakamlar kampanya tablosundakiyle aynı kaynaktan gelir. Aynı içeriğin Facebook ve Instagram kopyaları Meta'da
        ayrı reklamdır; görsel tekrarlanmasın diye kreatif bazında teklenir, rozetteki reklam sayısı ham adedi verir.
        Video kreatiflerinde önizleme karesi Meta'dan boş/siyah gelebilir.
      </p>
    </div>
  );
}

function Panel({ cfg }) {
  const [since, setSince] = useState(cfg.defaultSince);
  const [until, setUntil] = useState(cfg.defaultUntil);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState({});     // açık kreatif satırları
  const [showOther, setShowOther] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/canon-report/by-name`, {
        params: { group: cfg.group, since, until },
        headers: { 'X-Brand-ID': CANON_BRAND_ID },
        withCredentials: true,
      });
      setData(d);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [cfg.group, since, until]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const t = data?.totals;
  const plan = data?.plan;
  const isTraffic = cfg.primary === 'traffic';
  // Video izlenmesi etkileşimin kayda değer bir kısmıysa, statik içerikle aynı
  // sütunda kıyaslanmasın diye ana metrik video-hariç etkileşim olur.
  const videoHeavy = t && t.engagement > 0 && t.videoViews / t.engagement > 0.2;

  return (
    <section className="bg-white rounded-2xl shadow-sm border overflow-hidden">
      {/* Başlık + tarih aralığı */}
      <div className="px-5 py-4 border-b flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-bold text-gray-900" style={{ color: CANON_RED }}>{cfg.title}</h2>
            {data?.frame && (
              <span className="text-[9px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
                {data.frame}
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">{cfg.subtitle}</p>
          {/* Planlanan bütçe başlıkta görünsün — tabloya inmeden okunabilsin. */}
          {data && plan && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px]">
              <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                Planlanan bütçe <strong className="tabular-nums">{money(plan.net)}</strong> NET
                {plan.total && <span className="text-gray-400"> (brüt {money(plan.total)})</span>}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">
                Harcanan <strong className="tabular-nums">{money(t.spend)}</strong>
              </span>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                Kalan <strong className="tabular-nums">{money(Math.max(0, plan.net - t.spend))}</strong>
              </span>
            </div>
          )}
        </div>
        <div className="flex items-end gap-2">
          <label className="text-[10px] text-gray-500">
            Başlangıç
            <input type="date" value={since} onChange={(e) => setSince(e.target.value)}
              className="block border rounded-lg px-2 py-1 text-xs text-gray-800" />
          </label>
          <label className="text-[10px] text-gray-500">
            Bitiş
            <input type="date" value={until} onChange={(e) => setUntil(e.target.value)}
              className="block border rounded-lg px-2 py-1 text-xs text-gray-800" />
          </label>
          <button onClick={load} disabled={loading}
            className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-1.5 rounded-lg disabled:opacity-60"
            style={{ background: CANON_RED }}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Yenile
          </button>
        </div>
      </div>

      {error && (
        <div className="m-5 rounded-xl border border-red-200 bg-red-50 p-3 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-red-700">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="p-10 flex justify-center text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      )}

      {data && (
        <div className="p-5 space-y-5">
          {/* KPI — CPM her panelde ayrı kutu; alt satırda gömülü kalmasın diye. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2.5">
            <Tile label="Harcama" value={money(t.spend)} sub={`${data.campaigns.length} kampanya`} tone="red" />
            <Tile label="Gösterim" value={fmt(t.impressions)} />
            <Tile label="Tekil Erişim" value={fmt(t.reach)} sub={`frekans ${dec(t.frequency)}`} tone="green" />
            {isTraffic ? (
              <Tile label="Tıklama" value={fmt(t.clicks)} sub={`link tık ${fmt(t.linkClicks)}`} />
            ) : (
              <Tile
                label={videoHeavy ? 'Etkileşim (video hariç)' : 'Etkileşim'}
                value={fmt(videoHeavy ? t.engagementExVideo : t.engagement)}
                sub={pct(videoHeavy ? t.engagementExVideoRate : t.engagementRate) + ' oran'}
                tone="green"
              />
            )}
            <Tile label="CTR" value={pct(t.ctr)} sub={`CPC ${dec(t.cpc)} ₺`} />
            <Tile label="CPM" value={`${dec(t.cpm)} ₺`} sub="bin gösterim maliyeti" tone="indigo" />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2.5">
            <Tile label="Link Tıklama" value={fmt(t.linkClicks)} />
            <Tile label="Landing Page View" value={fmt(t.landingPageViews)} />
            <Tile label="Reaksiyon" value={fmt(t.reactions)} />
            <Tile label="Yorum" value={fmt(t.comments)} />
            <Tile label="Paylaşım" value={fmt(t.shares)} />
            <Tile label="Kaydetme" value={fmt(t.saves)} />
          </div>

          {videoHeavy && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-indigo-800 leading-relaxed">
                Toplam etkileşimin <strong>{fmt(t.videoViews)}</strong> adedi (%{((t.videoViews / t.engagement) * 100).toFixed(0)})
                video izlenmesidir. THRUPLAY'e optimize video reklamlarda <code>post_engagement</code> ağırlıklı olarak
                video izlemeden oluşur; statik içerikle aynı sütunda kıyaslanınca etkileşim oranını yapay olarak
                şişirir. Bu yüzden ana metrik <strong>video hariç etkileşim</strong> ({fmt(t.engagementExVideo)},
                %{t.engagementExVideoRate.toFixed(2)}) olarak gösterilir.
              </p>
            </div>
          )}

          {/* CEA sheet — satır satır plan vs gerçekleşen.
              Bütçe medya planı sayfasından DEĞİL, müşterinin takip ettiği CEA
              sheet'inden gelir; her satır kendi rakamını taşır. */}
          {data.sheetRows?.length > 0 && (() => {
            // Referans bütçe PLANIN kendisi; sheet toplamı yalnızca fark notunda.
            const budget = plan?.net || data.sheetPlanTotal;
            const planTotal = data.sheetPlanTotal;
            const ratio = budget ? t.spend / budget : null;
            return (
              <div className="rounded-xl border border-gray-200 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-sm font-bold text-gray-800">CEA Sheet — Plan vs Gerçekleşen</p>
                    <p className="text-[10px] text-gray-500">
                      Bütçeler CEA sheet'inin Temmuz bloğundaki <strong>Total Budget</strong> sütunundan, satır satır
                    </p>
                  </div>
                  {plan && (
                    <a href={plan.route} className="text-[11px] font-semibold hover:underline text-right" style={{ color: CANON_RED }}>
                      {plan.label} →
                      <span className="block text-[9px] font-normal text-gray-400">NET {money(plan.net)}</span>
                    </a>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2.5">
                  <Tile
                    label="Plan Bütçesi"
                    value={money(budget)}
                    sub={plan?.total ? `brüt ${money(plan.total)}` : (plan ? `${plan.label} içinden` : `${data.sheetRows.length} sheet satırı`)}
                  />
                  <Tile label="Gerçekleşen Harcama" value={money(t.spend)} sub={`${data.campaigns.length} kampanya`} tone="red" />
                  <Tile
                    label="Bütçe Gerçekleşme"
                    value={pct(ratio * 100)}
                    sub={t.spend > budget ? `${money(t.spend - budget)} aşım` : `kalan ${money(budget - t.spend)}`}
                    tone={ratio > 1.02 ? 'amber' : 'green'}
                  />
                  <Tile label="Gösterim / Erişim" value={fmt(t.impressions)} sub={`${fmt(t.reach)} tekil erişim`} />
                </div>
                {plan?.groupNote && (
                  <p className="text-[10px] text-gray-400 mt-1.5">{plan.groupNote}</p>
                )}

                {/* Sheet satırları planın tamamını kapsamayabilir — fark açıkça yazılır. */}
                {plan && Math.abs(budget - planTotal) > 100 && (
                  <p className="text-[10px] text-gray-500 mt-2 rounded-lg bg-gray-50 border border-gray-200 px-2.5 py-1.5">
                    Planda <strong>{money(budget)}</strong> bütçe var, CEA sheet'inde satırlanmış tutar{' '}
                    <strong>{money(planTotal)}</strong> —{' '}
                    <strong className="text-amber-700">{money(Math.abs(budget - planTotal))}</strong>
                    {budget > planTotal
                      ? ' henüz sheet\'e içerik olarak girilmemiş.'
                      : ' sheet\'te plan bütçesini aşıyor.'}
                  </p>
                )}

                <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                        <th className="text-left px-3 py-2 font-semibold">Sheet Satırı</th>
                        <th className="text-left px-3 py-2 font-semibold">Yayın</th>
                        <th className="text-right px-3 py-2 font-semibold">Plan</th>
                        <th className="text-right px-3 py-2 font-semibold">Harcanan</th>
                        <th className="text-right px-3 py-2 font-semibold">Gerçekleşme</th>
                        <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                        <th className="text-right px-3 py-2 font-semibold">Erişim</th>
                        <th className="text-right px-3 py-2 font-semibold">Tık</th>
                        <th className="text-right px-3 py-2 font-semibold">CTR</th>
                        <th className="text-right px-3 py-2 font-semibold">CPM</th>
                        <th className="text-right px-3 py-2 font-semibold">Kmp.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {data.sheetRows.map((r) => {
                        // %85 altı = ciddi eksik harcama, %102 üstü = aşım.
                        const tone = r.spendRatio == null ? 'text-gray-400'
                          : r.spendRatio < 0.85 ? 'text-amber-700 font-semibold'
                            : r.spendRatio > 1.02 ? 'text-red-600 font-semibold' : 'text-emerald-700';
                        return (
                          <tr key={r.id} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-2 max-w-xs">
                              <p className="font-semibold text-gray-800">{r.pillar}</p>
                              <span className="text-[9px] text-gray-400">{r.objective}</span>
                              {r.note && (
                                <p className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
                                  <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />{r.note}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{r.flight}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{money(r.budget)}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-semibold">
                              {r.actual ? money(r.actual.spend) : '—'}
                            </td>
                            <td className={`px-3 py-2 text-right tabular-nums ${tone}`}>
                              {r.spendRatio == null ? 'eşleşme yok' : pct(r.spendRatio * 100)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.actual ? fmt(r.actual.impressions) : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.actual ? fmt(r.actual.reach) : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.actual ? fmt(r.actual.clicks) : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.actual ? pct(r.actual.ctr) : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.actual ? `${dec(r.actual.cpm)} ₺` : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-gray-500">{r.campaignCount}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="bg-red-50 font-bold text-gray-800">
                        <td className="px-3 py-2" colSpan={2}>TOPLAM</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(planTotal)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{money(t.spend)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(ratio * 100)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(t.impressions)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(t.reach)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{fmt(t.clicks)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{pct(t.ctr)}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{dec(t.cpm)} ₺</td>
                        <td className="px-3 py-2 text-right tabular-nums">{data.campaigns.length}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  Satır gerçekleşenleri de <strong>tekilleştirilmiş</strong> erişimle hesaplanır — bir sheet satırına
                  birden çok kampanya düşebiliyor (FB/IG kopyaları, "- Copy" ikizleri). Seçili tarih aralığı satırın
                  yayın süresini kapsamıyorsa gerçekleşme doğal olarak düşük görünür.
                </p>

                {data.sheetUnmatched?.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                    <p className="text-[11px] font-bold text-amber-900 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Sheet'te karşılığı olmayan kampanyalar ({data.sheetUnmatched.length})
                    </p>
                    <p className="text-[10px] text-amber-800 mt-0.5">
                      Meta'da yayınlanmış ama CEA sheet'inde satırı bulunmayan harcamalar.
                    </p>
                    <div className="mt-1.5 space-y-1">
                      {data.sheetUnmatched.map((c) => (
                        <div key={c.id} className="text-[11px] text-amber-900 flex gap-2">
                          <span className="font-semibold tabular-nums w-20 text-right flex-shrink-0">{money(c.spend)}</span>
                          <span className="min-w-0 break-words">
                            {cleanName(c.name)}
                            <span className="text-[9px] text-amber-700 ml-1.5">{c.format?.label} · {c.start}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Alt kırılım */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">{cfg.bucketTitle}</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="text-left px-3 py-2 font-semibold">{cfg.bucketHead}</th>
                    <th className="text-right px-3 py-2 font-semibold">Kampanya</th>
                    <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Tekil Erişim</th>
                    <th className="text-right px-3 py-2 font-semibold">Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">CTR</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">Etkileşim</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.buckets.map((b) => (
                    <tr key={b.key} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-semibold text-gray-800">{b.label}</td>
                      <td className="px-3 py-2 text-right text-gray-500 tabular-nums">{b.campaignCount}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(b.totals.spend)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(b.totals.impressions)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(b.totals.reach)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{fmt(b.totals.clicks)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{pct(b.totals.ctr)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{dec(b.totals.cpc)} ₺</td>
                      <td className="px-3 py-2 text-right tabular-nums">{dec(b.totals.cpm)} ₺</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {fmt(b.totals.videoViews / Math.max(1, b.totals.engagement) > 0.2
                          ? b.totals.engagementExVideo : b.totals.engagement)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-red-50 font-bold text-gray-800">
                    <td className="px-3 py-2">TOPLAM</td>
                    <td className="px-3 py-2 text-right tabular-nums">{data.campaigns.length}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(t.spend)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.impressions)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.reach)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmt(t.clicks)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{pct(t.ctr)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dec(t.cpc)} ₺</td>
                    <td className="px-3 py-2 text-right tabular-nums">{dec(t.cpm)} ₺</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {fmt(videoHeavy ? t.engagementExVideo : t.engagement)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="text-[10px] text-gray-400 mt-2">
              Erişim, Meta'nın <strong>tekilleştirdiği</strong> rakamdır (hesap seviyesinde kampanya filtresiyle çekilir).
              Kampanya satırlarındaki erişimin ham toplamı {fmt(data.reachSumOfCampaigns)} — aradaki
              {' '}{fmt(data.reachSumOfCampaigns - t.reach)} kişilik fark kampanyalar arası çakışmadır, satırlar toplanmamalıdır.
            </p>
          </div>

          {/* MEDYA — sadece cfg.media açık panellerde; kreatif çağrısı hesabın
              tüm reklamlarını çektiği için her panelde otomatik açılmaz. */}
          {cfg.media && data.campaigns.length > 0 && (
            <MediaBlock campaigns={data.campaigns} since={data.since} until={data.until} />
          )}

          {/* Kampanyalar */}
          <div>
            <p className="text-sm font-bold text-gray-800 mb-2">Kampanyalar ({data.campaigns.length})</p>
            <div className="overflow-x-auto rounded-xl border border-gray-200">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                    <th className="px-2 py-2 w-6" />
                    <th className="text-left px-3 py-2 font-semibold">Kampanya</th>
                    <th className="text-left px-3 py-2 font-semibold">Akış</th>
                    <th className="text-right px-3 py-2 font-semibold">Harcama</th>
                    <th className="text-right px-3 py-2 font-semibold">Gösterim</th>
                    <th className="text-right px-3 py-2 font-semibold">Erişim</th>
                    <th className="text-right px-3 py-2 font-semibold">Tık</th>
                    <th className="text-right px-3 py-2 font-semibold">CTR</th>
                    <th className="text-right px-3 py-2 font-semibold">CPC</th>
                    <th className="text-right px-3 py-2 font-semibold">CPM</th>
                    <th className="text-right px-3 py-2 font-semibold">Etkileşim</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.campaigns.map((c) => {
                    const isOpen = !!open[c.id];
                    const cVideoHeavy = c.engagement > 0 && c.videoViews / c.engagement > 0.2;
                    return (
                      <React.Fragment key={c.id}>
                        <tr className="hover:bg-gray-50 align-top">
                          <td className="px-2 py-2">
                            <button onClick={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))}
                              className="text-gray-400 hover:text-gray-700">
                              {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                            </button>
                          </td>
                          <td className="px-3 py-2 max-w-md">
                            <p className="font-semibold text-gray-800 break-words">{cleanName(c.name)}</p>
                            <div className="flex flex-wrap items-center gap-1 mt-1">
                              {/* Birincil rozet panelin kırılım eksenidir; format
                                  ekseni değilse format ayrıca gri rozet olarak gösterilir. */}
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100">
                                {data.bucketBy === 'format' ? c.format.label : c.familyLabel}
                              </span>
                              {data.bucketBy !== 'format' && (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                                  {c.format.label}
                                </span>
                              )}
                              <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${STATUS_CLS[c.status] || 'bg-gray-100 text-gray-600'}`}>
                                {c.status}
                              </span>
                              {c.sheetPillar ? (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  sheet: {c.sheetPillar}
                                </span>
                              ) : (
                                <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                                  sheet'te yok
                                </span>
                              )}
                              <span className="text-[9px] text-gray-400">{c.matchedOn}</span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{c.start || '—'}<br />{c.stop || '—'}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(c.spend)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(c.impressions)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(c.reach)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{fmt(c.clicks)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{pct(c.ctr)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">{dec(c.cpc)} ₺</td>
                          <td className="px-3 py-2 text-right tabular-nums">{dec(c.cpm)} ₺</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {fmt(cVideoHeavy ? c.engagementExVideo : c.engagement)}
                            {cVideoHeavy && (
                              <span className="block text-[9px] text-indigo-500">+{fmt(c.videoViews)} video izl.</span>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={11} className="p-0 border-t border-gray-100">
                              <CreativeRows campaignId={c.id} since={data.since} until={data.until} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Şeffaflık: aile eşleşti ama tarih kuralına takıldı */}
          {data.excluded.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-bold text-amber-900">
                    Aile adı eşleşti ama rapora alınmadı ({data.excluded.length})
                  </p>
                  <p className="text-[11px] text-amber-800 mt-0.5">
                    Bu kampanyalar aynı içerik ailesinin adını taşıyor fakat önceki aya ait; harcamaları seçili
                    pencereye taştığı için isim eşleşmesi tek başına yeterli değil. Sessizce toplanmamaları için
                    burada listelenir.
                  </p>
                  <div className="mt-2 space-y-1.5">
                    {data.excluded.map((c) => (
                      <div key={c.id} className="text-[11px] text-amber-900 bg-white/70 rounded-lg px-2.5 py-1.5">
                        <span className="font-semibold tabular-nums">{money(c.spend)}</span>
                        {' · '}{cleanName(c.name)}
                        <span className="block text-[10px] text-amber-700 mt-0.5">{c.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Şeffaflık: bu gruba girmeyen harcamalar */}
          {data.others.length > 0 && (
            <div className="rounded-xl border border-gray-200 p-4">
              <button onClick={() => setShowOther((v) => !v)} className="flex items-center gap-1.5 text-sm font-bold text-gray-800">
                {showOther ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                Bu gruba girmeyen diğer harcamalar ({data.others.length}) —{' '}
                {money(data.others.reduce((s, c) => s + c.spend, 0))}
              </button>
              <p className="text-[10px] text-gray-500 mt-1 ml-6">
                Seçili pencerede hesapta harcaması olan ama bu rapora ait olmayan kampanyalar. Hesap toplamı ile
                rapor toplamı arasındaki farkın nereden geldiği görünsün diye listelenir.
              </p>
              {showOther && (
                <div className="mt-2 space-y-1">
                  {data.others.map((c) => (
                    <div key={c.id} className="text-[11px] text-gray-700 flex gap-2">
                      <span className="font-semibold tabular-nums w-20 text-right flex-shrink-0">{money(c.spend)}</span>
                      <span className="min-w-0 break-words">
                        {cleanName(c.name)}
                        {c.note && <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{c.note}</span>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export default function CanonLiveNameReport() {
  const [exporting, setExporting] = useState(false);
  const rootRef = React.useRef(null);

  const exportPdf = useCallback(async () => {
    if (exporting || !rootRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(rootRef.current, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/jpeg', 0.95);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      const m = 8;
      const w = pw - m * 2;
      const h = (canvas.height * w) / canvas.width;
      let y = m, rem = h;
      pdf.addImage(img, 'JPEG', m, y, w, h);
      rem -= (ph - m * 2);
      while (rem > 0) {
        pdf.addPage();
        y = m - (h - rem);
        pdf.addImage(img, 'JPEG', m, y, w, h);
        rem -= (ph - m * 2);
      }
      pdf.save('Canon_Canli_Kampanya_Raporu.pdf');
    } catch (e) {
      console.error('PDF export failed:', e);
    } finally {
      setExporting(false);
    }
  }, [exporting]);

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Canon — Canlı Kampanya Raporu</h1>
          <p className="text-xs text-gray-500 mt-1 max-w-3xl leading-relaxed">
            Meta hesabındaki işler CEA sheet'inin kendi taksonomisiyle dört başlıkta raporlanır:
            {' '}<strong>Kadrajın Ötesinde</strong> video yarışması, <strong>PV Summer Promo</strong>,
            {' '}<strong>UGC</strong> (sheet'te «Future of People») ve{' '}
            <strong>Future of What's Possible</strong> (Tuesday Tip + Technology Highlight).
            Reklam ve adset adları jenerik bırakıldığı için ayrım <strong>kampanya adından</strong> yapılır;
            veriler Meta Graph API'den <strong>canlı</strong> çekilir.
          </p>
        </div>
        <button onClick={exportPdf} disabled={exporting}
          className="flex items-center gap-1.5 text-white text-xs font-semibold px-3 py-2 rounded-lg disabled:opacity-60"
          style={{ background: CANON_RED }}>
          {exporting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Printer className="w-3.5 h-3.5" />}
          PDF
        </button>
      </div>

      <div ref={rootRef} className="space-y-5">
        {PANELS.map((cfg) => <Panel key={cfg.group} cfg={cfg} />)}
      </div>

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <p className="text-[11px] font-semibold text-gray-700">Eşleştirme nasıl yapılıyor?</p>
        <ul className="text-[11px] text-gray-600 mt-1.5 space-y-1 list-disc list-inside leading-relaxed">
          <li>
            Kampanya adı normalize edilir: Türkçe harfler ASCII'ye indirilir, <strong>görünmez karakterler
            (zero-width space)</strong> ve tüm boşluk/noktalama atılır. Hesaptaki adlarda
            {' '}<code>"Ya rışması"</code>, <code>"J uly2026"</code>, <code>"ÖtesindeVideo"</code> gibi bozulmalar
            olduğu için kelime eşitliği kullanılamaz, substring eşleşmesi yapılır.
          </li>
          <li>
            Gruplar <strong>sırayla</strong> denenir, ilk eşleşen kazanır:
            {' '}<strong>1)</strong> Yarışma (<code>kadraj</code>/<code>otesinde</code>/<code>yarism</code>)
            {' '}<strong>2)</strong> Summer Promo (<code>summerpromo</code>)
            {' '}<strong>3)</strong> UGC (adında <code>ugc</code> geçen her şey)
            {' '}<strong>4)</strong> Future of What's Possible (<code>technology highlight</code>, <code>tuesday tip</code>).
            Bu yüzden hem "ugc" hem "tuesday tip" geçen bir kampanya UGC'ye düşer — sheet'te de o satırlar
            Future of People altında.
          </li>
          <li><strong>Yarışmada tarih kısıtı yok</strong> — adlarda "June2026" yazsa da akış Ağustos'a kadar sürüyor, kısıt konsa kampanyalar düşerdi.</li>
          <li><strong>Temmuz gruplarında tarih kuralı şart:</strong> ya adında <code>July2026</code> etiketi ya da kampanyanın seçili pencerede başlamış olması. Bu kural olmadan Haziran kampanyaları rapora sızıyor.</li>
          <li>
            <strong>Plan bütçesi CEA sheet'inden satır satır</strong> gelir (medya planı sayfasının toplam NET'inden
            değil). Her kampanya, adı + formatı + başlangıç tarihiyle sheet'in Temmuz bloğundaki bir satıra
            oturtulur; sheet'te satırı olmayan harcamalar ayrıca uyarı olarak listelenir.
          </li>
          <li>Erişim rakamları Meta'nın <strong>tekilleştirdiği</strong> değerlerdir; kampanya satırları toplanmamalıdır.</li>
        </ul>
      </div>
    </div>
  );
}
